import { useEffect, useMemo, useState } from "react";

import type { OsanoConsent } from "@/types/window";

type ConsentCategories = {
  functionality: boolean;
  performance: boolean;
  targeting: boolean;
};

const DEFAULT_CONSENT: ConsentCategories = {
  functionality: false,
  performance: false,
  targeting: false,
};

function isAccepted(value: OsanoConsent[keyof OsanoConsent]): boolean {
  return value === "ACCEPT" || value === "ACCEPT_ALL";
}

function fromOsanoConsent(consent: OsanoConsent): ConsentCategories {
  return {
    performance: isAccepted(consent.ANALYTICS),
    targeting: isAccepted(consent.MARKETING),
    functionality: isAccepted(consent.PERSONALIZATION),
  };
}

function readFromOsano(): ConsentCategories | null {
  if (typeof window === "undefined") return null;

  const cm = window.Osano?.cm;

  if (cm?.getConsent) {
    try {
      return fromOsanoConsent(cm.getConsent());
    } catch {
      // ignore
    }
  }

  return null;
}

function readFromOsanoCookie(): ConsentCategories {
  if (typeof document === "undefined") return DEFAULT_CONSENT;

  const cookie = document.cookie
    .split("; ")
    .find((c) => c.startsWith("osano_consentmanager="));

  if (!cookie) return DEFAULT_CONSENT;

  try {
    const value = decodeURIComponent(cookie.split("=").slice(1).join("="));
    return fromOsanoConsent(JSON.parse(value));
  } catch {
    return DEFAULT_CONSENT;
  }
}

function readConsent(): ConsentCategories {
  return readFromOsano() ?? readFromOsanoCookie();
}

export function useGdprTracking(): ConsentCategories & {
  maskIdentifier(string: string): string;
} {
  const [consent, setConsent] = useState<ConsentCategories>(() =>
    readConsent(),
  );

  function maskIdentifier(string: string) {
    if (!string) return "";
    if (string.length <= 3) return "*".repeat(string.length);

    const visible = string.slice(0, 3);
    const masked = "*".repeat(string.length - 3);
    return visible + masked;
  }

  useEffect(() => {
    if (typeof window === "undefined") return;

    const sync = () => setConsent(readConsent());
    let registered = false;

    const registerListener = () => {
      if (registered || !window.Osano?.cm) return;
      registered = true;
      window.Osano.cm.addEventListener("osano-cm-consent-changed", sync);
      sync();
    };

    registerListener();
    window.addEventListener("osano-cm-initialized", registerListener);

    return () => {
      window.removeEventListener("osano-cm-initialized", registerListener);
      window.Osano?.cm?.removeEventListener("osano-cm-consent-changed", sync);
    };
  }, []);

  return useMemo(
    () => ({
      maskIdentifier,
      functionality: consent.functionality,
      performance: consent.performance,
      targeting: consent.targeting,
    }),
    [consent.targeting, consent.functionality, consent.performance],
  );
}
